import React from 'react';
import { FileText, Upload, X } from 'lucide-react';
import { unidadesApi } from '../../lib/api';
import { toast } from 'sonner';

/**
 * Celda reutilizable para subir / ver / quitar el documento de reserva de una unidad.
 * Cambia el estado de la unidad a "reservada" al subir y a "vacante" al quitar.
 * Si la unidad ya está alquilada, no se permite reservar (muestra "—").
 */
export const ReservaCell = ({ unidad, onUpdated, compact = false }) => {
  const [loading, setLoading] = React.useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    try {
      await unidadesApi.uploadReserva(unidad.unidad_id, file);
      toast.success('Reserva cargada. Unidad marcada como "Reservada".');
      onUpdated && onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al subir reserva');
    } finally {
      setLoading(false);
      e.target.value = '';
    }
  };

  const handleRemove = async () => {
    if (!window.confirm('¿Quitar la reserva? La unidad pasará a Vacante.')) return;
    setLoading(true);
    try {
      await unidadesApi.deleteReserva(unidad.unidad_id);
      toast.success('Reserva eliminada');
      onUpdated && onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar reserva');
    } finally {
      setLoading(false);
    }
  };

  if (unidad.estado_alquiler === 'alquilada') {
    return <span className="text-xs text-slate-300">—</span>;
  }

  const tieneReserva = !!(unidad.documento_reserva && (unidad.documento_reserva.archivo_id || unidad.documento_reserva.url));

  if (tieneReserva) {
    return (
      <div className="inline-flex items-center gap-1" data-testid={`reserva-cell-${unidad.unidad_id}`}>
        <a
          href={unidadesApi.downloadReserva(unidad.unidad_id)}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-purple-50 text-purple-700 rounded hover:bg-purple-100"
          title={`Ver reserva${unidad.documento_reserva?.nombre ? `: ${unidad.documento_reserva.nombre}` : ''}`}
          data-testid={`ver-reserva-${unidad.unidad_id}`}
        >
          <FileText className="w-3.5 h-3.5" />
          {compact ? '' : 'Ver'}
        </a>
        <label className="inline-flex items-center px-2 py-1 text-xs text-amber-600 hover:bg-amber-50 rounded cursor-pointer" title="Reemplazar">
          <Upload className="w-3.5 h-3.5" />
          <input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={handleUpload} disabled={loading} className="hidden" />
        </label>
        <button
          onClick={handleRemove}
          disabled={loading}
          className="px-1.5 py-1 text-xs text-red-500 hover:bg-red-50 rounded disabled:opacity-40"
          title="Quitar reserva"
          data-testid={`quitar-reserva-${unidad.unidad_id}`}
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  return (
    <label
      className={`inline-flex items-center gap-1 px-2 py-1 text-xs bg-purple-50 text-purple-700 rounded hover:bg-purple-100 cursor-pointer ${loading ? 'opacity-50 pointer-events-none' : ''}`}
      title="Subir documento de reserva"
      data-testid={`subir-reserva-${unidad.unidad_id}`}
    >
      <Upload className="w-3.5 h-3.5" />
      {loading ? 'Subiendo...' : 'Reservar'}
      <input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={handleUpload} disabled={loading} className="hidden" />
    </label>
  );
};

export default ReservaCell;
